import React, { useEffect, useRef, useState } from "react";
import "./style.scss";
import { useDispatch, useSelector } from "react-redux";
import { returnErrors } from "../../utils/store/reducers/Error";
import { useNavigate } from "react-router-dom";
import {
  digiSigner,
  getContractListRequest,
  resetRebillPayments,
} from "../../utils/apis/userApi";
import Table from "../../components/Table";
import RePaymentModal from "../../reusableComponents/RePaymentModal/RePaymentModal";
import { setContractId } from "../../utils/store/reducers/authSlice";
import { setLastPayment } from "../../utils/store/reducers/accountMetricsSlice";
import Spinner from "../../reusableComponents/spinner/Spinner";
import { formatDateInTimeZone } from "../../utils/helpers/string";
import { returnMessages } from "../../utils/store/reducers/Message";
import LoaderOverlay from "../../reusableComponents/LoaderOverlay/Index";

function ContractList() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const count = useRef(0);
  const { idToken } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [contractList, setContractList] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentPayload, setPaymentPayload] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [paymentIsLoading, setPaymentIsLoading] = useState(false);
  const [params, setParams] = useState({
    page: 1,
    page_size: 20,
  });

  const fetchContractList = async () => {
    setIsLoading(true);
    try {
      const response = await getContractListRequest(idToken, params);
      setContractList(response?.data);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchContractList();
  }, [idToken, JSON.stringify(params)]);

  const handleSubmitPayment = async () => {
    if (!paymentPayload) return;
    setPaymentIsLoading(true);
    try {
      const response = await resetRebillPayments(idToken, paymentPayload);

      if (response.status < 399) {
        dispatch(setLastPayment(response?.data?.data));
        window.location.replace(response?.data.url);
      } else if (response?.data?.detail === "No activation fee.") {
        dispatch(returnMessages(response.data.detail, 400));
      } else {
        dispatch(returnErrors(response.data.detail, 400));
      }
    } catch (error) {
      dispatch(returnErrors(error.message, error.response?.status || 500));
    } finally {
      setPaymentIsLoading(false);
      setPaymentModalOpen(false);
      setSelectedContract(null);
      fetchContractList();
    }
  };

  // const openPaymentModal = (contract) => {
  //   if (!contract) return;
  //   setSelectedContract(contract);
  //   setPaymentPayload({
  //     payment_method: "nuvei",
  //     payment_type: "activation_fees",
  //     account_id: contract?.account,
  //     contract_id: contract?.id,
  //     promo: "",
  //   });
  //   setPaymentModalOpen(true);
  // };

  const openPaymentModal = (contract) => {
    if (!contract) return;

    // Skip modal for Zero Plan
    if (contract?.plan_name === "Zero Plan") {
      const payload = {
        payment_method: "nuvei",
        payment_type: "activation_fees",
        account_id: contract?.account,
        contract_id: contract?.id,
        promo: "",
      };

      setPaymentIsLoading(true);

      resetRebillPayments(idToken, payload)
        .then((response) => {
          if (response.status < 399) {
            dispatch(setLastPayment(response?.data?.data));
            window.location.replace(response?.data?.url);
          } else if (response?.data?.detail === "No activation fee.") {
            dispatch(returnMessages(response.data.detail, 400));
          } else {
            dispatch(returnErrors(response.data.detail, 400));
          }
        })
        .catch((error) => {
          dispatch(returnErrors(error.message, error.response?.status || 500));
        })
        .finally(() => {
          setPaymentIsLoading(false);
          fetchContractList();
        });

      return;
    }

    setSelectedContract(contract);
    setPaymentPayload({
      payment_method: "nuvei",
      payment_type: "activation_fees",
      account_id: contract?.account,
      contract_id: contract?.id,
      promo: "",
    });
    setPaymentModalOpen(true);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
        Cell: ({ row }) => {
          return (
            <p
              style={{
                width: "30px",
                height: "30px",
                backgroundColor: "#071E0D",
                color: "white",
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
              }}
            >
              {count.current++}
            </p>
          );
        },
      },
      {
        Header: "Account Name",
        accessor: "account_name",
        Cell: ({ row }) => (
          <span style={{ fontSize: "12px", cursor: "pointer" }}>
            {row.original.account_name}
          </span>
        ),
      },
      {
        Header: "Funded Account",
        accessor: "funded_account",
        Cell: ({ row }) => (
          <span style={{ fontSize: "12px", cursor: "pointer" }}>
            {row.original.funded_account || "N/A"}
          </span>
        ),
      },
      {
        Header: "Verified",
        accessor: "is_verified",
        Cell: ({ row }) => (
          <>
            {row.original.is_verified ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <rect
                  width="24"
                  height="24"
                  rx="12"
                  fill="#209829"
                  fill-opacity="0.1"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M17.7344 7.22486C18.072 7.53992 18.0902 8.06897 17.7751 8.40653L10.8899 15.7836C10.7352 15.9493 10.5198 16.0451 10.2931 16.0491C10.0664 16.053 9.84783 15.9646 9.6875 15.8043L6.24488 12.3617C5.91837 12.0352 5.91837 11.5058 6.24488 11.1793C6.57138 10.8528 7.10075 10.8528 7.42725 11.1793L10.258 14.01L16.5527 7.26561C16.8678 6.92804 17.3968 6.9098 17.7344 7.22486Z"
                  fill="#209829"
                />
              </svg>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <rect
                    width="24"
                    height="24"
                    rx="12"
                    fill="#FA4D4D"
                    fill-opacity="0.1"
                  />
                  <path
                    d="M6.99979 18C6.80204 18 6.60874 17.9412 6.44433 17.8312C6.27992 17.7212 6.15178 17.5649 6.07611 17.382C6.00044 17.1991 5.98064 16.9979 6.0192 16.8037C6.05777 16.6096 6.15298 16.4312 6.29279 16.2912L16.2928 6.281C16.4814 6.09866 16.734 5.99776 16.9962 6.00004C17.2584 6.00232 17.5092 6.1076 17.6946 6.29319C17.88 6.47879 17.9852 6.72986 17.9875 6.99233C17.9897 7.25479 17.8889 7.50765 17.7068 7.69645L7.70679 17.7067C7.61405 17.7998 7.50385 17.8736 7.38252 17.924C7.26119 17.9743 7.13112 18.0001 6.99979 18Z"
                    fill="#FA4D4D"
                  />
                  <path
                    d="M16.9994 18C16.8681 18.0001 16.738 17.9743 16.6167 17.924C16.4954 17.8736 16.3852 17.7998 16.2924 17.7067L6.29243 7.69645C6.11027 7.50765 6.00948 7.25479 6.01176 6.99233C6.01403 6.72986 6.1192 6.47879 6.30461 6.29319C6.49002 6.1076 6.74083 6.00232 7.00303 6.00004C7.26523 5.99776 7.51783 6.09866 7.70643 6.281L17.7064 16.2912C17.8462 16.4312 17.9414 16.6096 17.98 16.8037C18.0186 16.9979 17.9988 17.1991 17.9231 17.382C17.8474 17.5649 17.7193 17.7212 17.5549 17.8312C17.3905 17.9412 17.1972 18 16.9994 18Z"
                    fill="#FA4D4D"
                  />
                </svg>
              </>
            )}
          </>
        ),
      },

      {
        Header: "Contract Signed",
        accessor: "contract_issued",
        Cell: ({ row }) => <>{row.original.contract_issued ? "Yes" : "No"}</>,
      },
      {
        Header: "Created on",
        accessor: "created_at",
        Cell: ({ row }) => <>{formatDateInTimeZone(row.original.created_at)}</>,
      },
      {
        Header: "Contract Status",
        accessor: "status",
        Cell: ({ value }) => (
          <span
            style={{
              textTransform: "capitalize",
              textAlign: "center",
              justifyContent: "center",
            }}
            className={`comp_status ${
              value === "rejected" || value === "revoked"
                ? "red_status"
                : value === "in_progress"
                ? "yellow_status"
                : value === "approved" && "green_status"
            }`}
          >
            {value.replace("_", " ")}
          </span>
        ),
      },
      {
        Header: "Payment Status",
        accessor: "payment_status",
        Cell: ({ value, row }) => {
          const planName = row?.original?.plan_name;

          const normalizedValue = value ? value.toLowerCase() : "";

          let statusClass = "";

          if (planName === "Zero Plan") {
            statusClass = "";
          } else if (normalizedValue === "denied") {
            statusClass = "red_status";
          } else if (normalizedValue === "new" || normalizedValue === "open") {
            statusClass = "yellow_status";
          } else if (normalizedValue === "succeeded") {
            statusClass = "green_status";
          }

          return (
            <span
              className={`comp_status ${statusClass}`}
              style={{
                textTransform: "capitalize",
                textAlign: "center",
                justifyContent: "center",
              }}
            >
              {planName === "Zero Plan"
                ? "-"
                : (value || "").replaceAll("_", " ")}
            </span>
          );
        },
      },
      {
        Header: "Sign Contract",
        accessor: "action",
        Cell: ({ row }) => {
          const [anchorEl, setAnchorEl] = React.useState(null);

          const open = Boolean(anchorEl);
          const handleClick = (event) => {
            setAnchorEl(event.currentTarget);
          };
          const handleClose = () => {
            setAnchorEl(null);
          };
          const [loading, setLoading] = useState(false);
          const dispatch = useDispatch();
          const [isSpinner, setIsSpinner] = useState(false);
          const handleSignContract = async (e) => {
            try {
              setIsSpinner(true);
              if (row.original.url) {
                window.location?.replace(row.original.url);
                dispatch(setContractId(row.original.id));
                setIsSpinner(false);
              } else {
                const response = await digiSigner(row.original.id, idToken);
                if (response?.status < 399) {
                  window.location.replace(response?.data?.url);
                  dispatch(setContractId(row.original.id));
                  setIsSpinner(false);
                } else {
                  throw new Error(response.data.detail);
                }
              }
            } catch (error) {
              dispatch(
                returnErrors(
                  error.message || "Something went wrong",
                  error.status || 500
                )
              );
              setIsSpinner(false);
              setLoading(false);
            } finally {
            }
          };

          return (
            <div>
              <div>
                <button
                  className="contract_payment_btn"
                  onClick={() =>
                    row.original.is_verified
                      ? "Contract signed"
                      : handleSignContract(row)
                  }
                >
                  {isSpinner ? (
                    <Spinner size={25} />
                  ) : row.original.is_verified ? (
                    "Contract signed"
                  ) : (
                    "Sign Contract"
                  )}
                </button>
              </div>
            </div>
          );
        },
      },
      {
        Header: "Action Payment",
        accessor: "a",
        Cell: ({ row, value }) => {
          console.log("first", value, row);
          return (
            <button
              className="contract_payment_btn"
              onClick={
                row?.original?.payment_status !== "succeeded"
                  ? () => openPaymentModal(row?.original)
                  : null
              }
            >
              {row?.original?.payment_status === "succeeded" ? "Paid" : "Pay"}
            </button>
          );
        },
      },
    ],
    [openPaymentModal]
  );
  return (
    <div className="ContractList box">
      {isLoading && <LoaderOverlay />}
      <Table
        columns={columns}
        data={contractList || []}
        params={params}
        setParams={setParams}
      />
      {/* Payment Modal */}
      {paymentModalOpen && selectedContract && paymentPayload && (
        <RePaymentModal
          open={paymentModalOpen}
          setOpen={setPaymentModalOpen}
          setPayload={setPaymentPayload}
          payload={paymentPayload}
          handleSubmit={handleSubmitPayment}
          id={selectedContract?.account}
          isLoading={paymentIsLoading}
          initialAmount={selectedContract?.purchase_price}
          heading="Activation Fees Payment"
          isCoupon={false}
          items={{
            ...selectedContract,
            login_id: selectedContract?.account,
          }}
        />
      )}
    </div>
  );
}

export default ContractList;
